import { SourcePreviewAuthor } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewAuthor.tsx";
import { SourcePreviewDescription } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewDescription.tsx";
import { SourcePreviewFooter } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewFooter.tsx";
import { SourcePreviewHeader } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewHeader.tsx";
import { SourcePreviewTitle } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewTitle.tsx";
import type { SourcePreviewProps } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewProps.ts";
import { useConversationSource } from "#features/conversations/hooks/useConversationSource.ts";
import type { WorkleapOfficevibeRecognitionCustomProperties } from "#types/types.ts";
import { Div, Tag, type TagProps, Text } from "@hopper-ui/components";
import { useMemo } from "react";
import "./OfficevibeRecognitionPreview.dark.css";

export const OfficevibeRecognitionPreview = ({ source }: SourcePreviewProps) => {
    const { customProperties } = source;
    const { prompt, categoryName, categoryTheme } = customProperties as WorkleapOfficevibeRecognitionCustomProperties;
    const { title, author, date, description } = useConversationSource(source);

    const cssToken = useMemo(() => {
        const _cssTokens: Record<string, { backgroundColor: string; tagBackgroundColor: TagProps["backgroundColor"]; tagTextColor: TagProps["color"] }> = {
            ElectricBlue: {
                backgroundColor: "decorative-option1",
                tagBackgroundColor: "decorative-option1-strong",
                tagTextColor: "decorative-option1"
            },
            Seaweed: {
                backgroundColor: "decorative-option2",
                tagBackgroundColor: "decorative-option2-strong",
                tagTextColor: "decorative-option2"
            },
            Creamsicle: {
                backgroundColor: "decorative-option3",
                tagBackgroundColor: "decorative-option3-strong",
                tagTextColor: "decorative-option3"
            },
            Coral: {
                backgroundColor: "decorative-option4",
                tagBackgroundColor: "decorative-option4-strong",
                tagTextColor: "decorative-option4"
            },
            Sky: {
                backgroundColor: "decorative-option5",
                tagBackgroundColor: "decorative-option5-strong",
                tagTextColor: "decorative-option5"
            },
            LightSky: {
                backgroundColor: "decorative-option5",
                tagBackgroundColor: "decorative-option5-strong",
                tagTextColor: "decorative-option5"
            },
            Dandelion: {
                backgroundColor: "decorative-option6",
                tagBackgroundColor: "decorative-option6-strong",
                tagTextColor: "decorative-option6"
            },
            StrawberryFields: {
                backgroundColor: "decorative-option8",
                tagBackgroundColor: "decorative-option8-strong",
                tagTextColor: "decorative-option8"
            },
            Neutral: {
                backgroundColor: "decorative-option7",
                tagBackgroundColor: "decorative-option7-strong",
                tagTextColor: "decorative-option7"
            }
        };

        if (!categoryTheme) {
            return _cssTokens.Neutral;
        }

        return _cssTokens[categoryTheme];
    }, [categoryTheme]);

    return (
        <div className="wai-source-card">
            <SourcePreviewHeader source={source} date={date} />
            <SourcePreviewTitle text={title} />
            <Div
                UNSAFE_marginTop="0.625rem"
                borderRadius="rounded-md"
                UNSAFE_backgroundColor={cssToken.backgroundColor}
                padding="inset-md"
            >
                <Div>
                    <Text
                        fontSize="body-sm-semibold"
                        fontWeight="body-sm-semibold"
                        color="neutral"
                        className="wai-officevibe-recognition-prompt"
                    >
                        {prompt}
                    </Text>
                </Div>
                {categoryName && <Div marginTop="inline-xs">
                    <Tag
                        size="sm"
                        backgroundColor={cssToken.tagBackgroundColor}
                        color={cssToken.tagTextColor}
                        border="none"
                        UNSAFE_minHeight="1.25rem"
                    >
                        {categoryName}
                    </Tag>
                </Div>}
            </Div>
            {author && <SourcePreviewAuthor author={author} />}
            {description && <SourcePreviewDescription description={description} />}
            <SourcePreviewFooter source={source} />
        </div>
    );
};
